import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';
import { api } from '../lib/api';
import type { User } from '@home-guard/shared';

const RESEND_COOLDOWN_SEC = 60;

interface MeResponse {
  data: User;
}

export function VerifyPendingPage() {
  const navigate = useNavigate();
  const { user, setAuth, logout } = useAuthStore();

  const [cooldown, setCooldown] = useState(0);
  const [justSent, setJustSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [resendError, setResendError] = useState('');

  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // If already verified, redirect away
  useEffect(() => {
    if (user?.email_verified) {
      navigate('/app/home/setup', { replace: true });
    }
  }, [user, navigate]);

  // Auto-poll every 10 seconds to check if verified
  const checkVerified = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get<MeResponse>('/auth/me');
      if (res?.data?.email_verified) {
        // Update the auth store with the verified user (keep existing token/expiry)
        setAuth(res.data, useAuthStore.getState().accessToken ?? '', 900);
        navigate('/app/home/setup', { replace: true });
      }
    } catch {
      // Network hiccup — try again next poll
    }
  }, [user, setAuth, navigate]);

  useEffect(() => {
    pollRef.current = setInterval(checkVerified, 10_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [checkVerified]);

  // Cooldown ticker
  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  function startCooldown() {
    setCooldown(RESEND_COOLDOWN_SEC);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleResend() {
    if (cooldown > 0 || sending || !user) return;
    setSending(true);
    setResendError('');
    try {
      await api.post('/auth/resend-verification', {});
      setJustSent(true);
      startCooldown();
      setTimeout(() => setJustSent(false), 3000);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Something went wrong. Please try again.';
      setResendError(msg);
    } finally {
      setSending(false);
    }
  }

  function handleStartOver() {
    logout();
    navigate('/register', { replace: true });
  }

  const email = user?.email;

  return (
    <div style={{
      minHeight: '100dvh',
      background: '#0D0117',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 20px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 480,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
      }}>

        {/* Envelope illustration */}
        <div style={{
          width: 96,
          height: 96,
          background: 'rgba(147, 51, 234, 0.15)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 48,
          marginBottom: 28,
          border: '2px solid rgba(147, 51, 234, 0.3)',
        }}>
          ✉️
        </div>

        {/* Heading */}
        <h1 style={{
          margin: '0 0 12px',
          fontSize: 28,
          fontWeight: 700,
          color: '#ffffff',
          textAlign: 'center',
          letterSpacing: '-0.5px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          Check your email
        </h1>

        {/* Subheading */}
        <p style={{
          margin: '0 0 16px',
          fontSize: 15,
          color: 'rgba(255,255,255,0.6)',
          textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          We sent a verification link to
        </p>

        {/* Email pill */}
        {email && (
          <div style={{
            background: 'rgba(147, 51, 234, 0.18)',
            border: '1px solid rgba(147, 51, 234, 0.4)',
            borderRadius: 24,
            padding: '6px 18px',
            marginBottom: 24,
          }}>
            <span style={{
              fontSize: 15,
              fontWeight: 500,
              color: '#c084fc',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}>
              {email}
            </span>
          </div>
        )}

        {/* Body text */}
        <p style={{
          margin: '0 0 36px',
          fontSize: 14,
          color: 'rgba(255,255,255,0.55)',
          textAlign: 'center',
          lineHeight: 1.7,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          maxWidth: 380,
        }}>
          Click the link in the email to activate your Housewizrd account.
          It may take a minute or two to arrive. Check your spam folder if
          you don't see it.
        </p>

        {/* Resend button */}
        <button
          onClick={handleResend}
          disabled={cooldown > 0 || sending || !user}
          style={{
            width: '100%',
            padding: '14px',
            background: 'transparent',
            color: cooldown > 0 || sending ? 'rgba(255,255,255,0.35)' : '#ffffff',
            border: `1.5px solid ${cooldown > 0 || sending ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.55)'}`,
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 600,
            cursor: cooldown > 0 || sending || !user ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            marginBottom: 12,
          }}
        >
          {justSent
            ? '✓ Email sent!'
            : sending
            ? 'Sending…'
            : cooldown > 0
            ? `Resend in ${cooldown}s`
            : 'Resend verification email'}
        </button>

        {resendError && (
          <p style={{
            margin: '0 0 12px',
            fontSize: 13,
            color: '#f87171',
            textAlign: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}>
            {resendError}
          </p>
        )}

        {/* Polling indicator */}
        <p style={{
          margin: '0 0 40px',
          fontSize: 12,
          color: 'rgba(255,255,255,0.3)',
          textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          Checking automatically every 10 seconds…
        </p>

        {/* Start over */}
        <p style={{
          margin: 0,
          fontSize: 13,
          color: 'rgba(255,255,255,0.4)',
          textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          Wrong email address?{' '}
          <button
            onClick={handleStartOver}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'rgba(192, 132, 252, 0.9)',
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
          >
            Start over
          </button>
        </p>
      </div>
    </div>
  );
}
